import { Layout } from "@/components/layout/Layout";
import { usePageMeta } from "@/hooks/use-page-meta";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

export default function Experience() {
  usePageMeta("Professional Experience", "A snapshot of two decades spent building internet businesses, software, and operational systems.");

  const themes = [
    "Building/operating internet businesses", 
    "Technology/software development", 
    "Ecommerce/digital marketing", 
    "Business development/strategic partnerships", 
    "Payment processing/risk management", 
    "Healthcare/telehealth technology", 
    "Digital media/content systems", 
    "Team/project leadership", 
    "Vendor/client/stakeholder management"
  ];

  return (
    <Layout>
      <div className="container mx-auto px-6 max-w-4xl py-12 md:py-20">
        <header className="mb-16">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-medium tracking-tight mb-6">
            Professional Experience
          </h1>
          <p className="text-xl text-muted-foreground leading-relaxed max-w-2xl">
            A structural breakdown of the domains, systems, and environments I've operated in over the last two decades.
          </p>
        </header>

        <section className="mb-20">
          <h2 className="text-2xl font-serif font-medium mb-8 border-b border-border pb-4">Core Experience Themes</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {themes.map(theme => (
              <div key={theme} className="flex items-center gap-3 p-4 bg-secondary/30 rounded-xl border border-border/50">
                <div className="w-2 h-2 rounded-full bg-accent shrink-0" />
                <span className="font-medium">{theme}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-20 space-y-12">
          <h2 className="text-2xl font-serif font-medium mb-8 border-b border-border pb-4">Career Timeline</h2>
          
          {/* Placeholder eras. The prompt specifies NOT to invent specific employers or dates, just thematic eras. */}
          <div className="relative pl-8 md:pl-0">
            <div className="hidden md:block absolute left-[120px] top-2 bottom-0 w-px bg-border z-0"></div>
            
            {[
              {
                era: "[Recent Era]",
                title: "[Current / Recent Leadership Role]",
                desc: "[Description of recent work focusing on building complex systems, managing teams, and directing high-level strategy for businesses in healthcare, media, or ecommerce.]"
              },
              {
                era: "[Middle Era]",
                title: "[Previous Technical / Operational Role]",
                desc: "[Description of hands-on technical architecture, scaling platforms, running marketing operations, and managing the day-to-day chaos of growing digital businesses.]"
              },
              {
                era: "[Early Era]",
                title: "[Foundational Experience]",
                desc: "[Description of foundational work in web development, early ecommerce, and learning the raw mechanics of building digital products from scratch.]"
              }
            ].map((item, i) => (
              <div key={i} className="relative md:flex gap-12 mb-16 z-10">
                <div className="md:w-[120px] shrink-0 pt-1 relative">
                  <div className="absolute -left-10 md:left-[116px] top-2 w-3 h-3 rounded-full bg-background border-2 border-accent"></div>
                  <span className="text-sm font-semibold uppercase tracking-wider text-muted-foreground bg-background pr-4">{item.era}</span>
                </div>
                <div className="mt-4 md:mt-0 bg-card border border-border rounded-2xl p-6 md:p-8 flex-1">
                  <h3 className="text-xl font-medium mb-4">{item.title}</h3>
                  <p className="text-muted-foreground leading-relaxed italic">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-20">
          <h2 className="text-2xl font-serif font-medium mb-8 border-b border-border pb-4">Platforms & Tools</h2>
          <div className="bg-card border border-border rounded-2xl p-8">
            <p className="text-muted-foreground italic mb-6">
              [Placeholder for a detailed list of specific platforms, languages, and tools. e.g. React, TypeScript, Node, Shopify Plus, Stripe, AWS, GCP, Google Analytics 4, Tag Manager, specific headless CMS platforms, etc.]
            </p>
          </div>
        </section>

        <div className="bg-accent text-accent-foreground rounded-3xl p-10 md:p-16 text-center">
          <h2 className="text-3xl font-serif font-medium mb-6">Have a specific project in mind?</h2>
          <p className="text-accent-foreground/80 mb-8 max-w-2xl mx-auto text-lg">
            Résumés only tell part of the story. The best way to figure out if I can help is to have a conversation about what you're trying to build.
          </p>
          <Link href="/contact">
            <Button variant="secondary" size="lg" className="rounded-full px-8 bg-background text-foreground hover:bg-background/90">
              Get in Touch <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>

      </div>
    </Layout>
  );
}
